import React from 'react';
import m from './MealTargetsStepMobile.module.css';
import s from '../DietPlanBuilder.module.css';
import { Toggle } from '../ui';
import { copy } from '../../copy';
import { fmtInt } from '../../lib/format';
import { MACRO_META_NATIVE } from './goalsStep.shared';
import { mixLabel, mixWidths } from './mealTargets.shared';
import { MealTargetsBanner } from './MealTargetsBanner';
import type { useMealTargetsStep } from '../../hooks/useMealTargetsStep';
import type { MacroKey } from '../../types';

type Model = ReturnType<typeof useMealTargetsStep>;

export function MealTargetsStepMobile({
  planKcal,
  t,
  onEditGoals,
}: {
  planKcal: number;
  t: Model;
  onEditGoals: () => void;
}) {
  return (
    <div className={m.page}>
      <section className={m.goal}>
        <div className={m.goalTop}>
          <div>
            <div className={m.label}>{copy.targets.dailyGoal}</div>
            <div className={m.kcal}><b>{fmtInt(planKcal)}</b> kcal</div>
          </div>
          <button type="button" className={s.btnLink} onClick={onEditGoals}>{copy.targets.editGoals}</button>
        </div>
        <div className={m.macros}>
          <span><b>{t.gp} g</b> protein</span>
          <span><b>{t.gc} g</b> carbs</span>
          <span><b>{t.gf} g</b> fats</span>
        </div>
      </section>

      <div className={s.segToggle} role="group" aria-label="Meal share source">
        <button type="button" className={s.segToggleBtn} data-on={t.split === 'template'} disabled={!t.canTemplate} onClick={t.useTemplate}>
          {copy.targets.modeTemplate}
        </button>
        <button type="button" className={s.segToggleBtn} data-on={t.split === 'standard'} onClick={t.useStandard}>
          {copy.targets.modeStandard}
        </button>
      </div>

      {t.rows.map(({ m: meal, i, T, source }) => {
        const off = meal.skip;
        const mix = mixWidths(T);
        return (
          <section key={meal.key} className={m.card} data-off={off || undefined}>
            <div className={m.cardHead}>
              <span className={m.swatch} style={{ background: meal.color }} />
              <div className={m.mealCopy}>
                <div className={m.mealName}>{meal.name}</div>
                <div className={m.mealSub}>{source}</div>
              </div>
              <Toggle on={!off} label={copy.targets.includeMeal(meal.name)} onChange={v => t.setSkip(i, !v)} />
            </div>
            <div className={m.fields}>
              <label className={m.field}>
                <span className={m.label}>{copy.targets.colStart}</span>
                <input type="time" value={meal.time} disabled={off} onChange={e => t.setTime(i, e.target.value)} />
              </label>
              <label className={m.field}>
                <span className={m.label}>{copy.targets.colEnd}</span>
                <input type="time" value={meal.endTime} disabled={off} onChange={e => t.setEndTime(i, e.target.value)} />
              </label>
              <label className={m.field}>
                <span className={m.label}>{copy.targets.colShare}</span>
                <div className={m.num}>
                  <input type="number" inputMode="decimal" disabled={off} value={off ? '' : meal.share} onChange={e => t.setShare(i, parseFloat(e.target.value) || 0)} />
                  <span>%</span>
                </div>
              </label>
              <label className={m.field}>
                <span className={m.label}>{copy.targets.colKcal}</span>
                <input type="number" inputMode="numeric" disabled={off} value={off ? '' : Math.round(T.k)} onChange={e => t.setKcal(i, parseFloat(e.target.value) || 0)} />
              </label>
            </div>
            <div className={m.grams}>
              {(['p', 'c', 'f'] as MacroKey[]).map(k => (
                <label key={k} className={m.field}>
                  <span className={m.label}>{MACRO_META_NATIVE[k].label}</span>
                  <div className={m.num}>
                    <input type="number" inputMode="decimal" disabled={off} value={off ? '' : Math.round(T[k] * 10) / 10} onChange={e => t.setMacro(i, k, parseFloat(e.target.value) || 0)} />
                    <span>g</span>
                  </div>
                </label>
              ))}
            </div>
            {!off && (
              <div className={m.mix}>
                <div className={m.bar} aria-hidden>
                  <span style={{ width: mix.p, background: MACRO_META_NATIVE.p.bg }} />
                  <span style={{ width: mix.c, background: MACRO_META_NATIVE.c.bg }} />
                  <span style={{ width: mix.f, background: MACRO_META_NATIVE.f.bg }} />
                </div>
                <span>{mixLabel(T)}</span>
              </div>
            )}
          </section>
        );
      })}

      <div className={m.total} data-ok={t.shareSum === 100 || undefined}>
        <div className={m.totalRow}>
          <span>{copy.targets.totalMeals}</span>
          <b data-ok={t.shareSum === 100}>{Math.round(t.totals.share * 10) / 10}%</b>
        </div>
        <div className={m.totalMeta}>
          {fmtInt(t.totals.k)} kcal · {Math.round(t.totals.p)} g P · {Math.round(t.totals.c)} g C · {Math.round(t.totals.f)} g F
        </div>
      </div>

      <MealTargetsBanner
        shareSum={t.shareSum}
        macroDrifts={t.macroDrifts}
        onScaleShares={t.scaleShares}
      />
    </div>
  );
}
